import { useState, useMemo } from 'react';
import { PropertyFilterProps } from '@cloudscape-design/components';

/**
 * Configuration for a single filterable property
 */
export interface FilterPropertyConfig {
  key: string;
  propertyLabel: string;
  groupValuesLabel: string;
  operators?: PropertyFilterProps.ComparisonOperator[];
  getValue: (item: any) => string | undefined;
}

/**
 * Generic hook for implementing PropertyFilter on any list of items
 * 
 * @example
 * const { query, setQuery, filteredItems, filteringProperties, filteringOptions } = usePropertyFilter({
 *   items: workflows,
 *   filterConfigs: [
 *     {
 *       key: 'name',
 *       propertyLabel: 'Name',
 *       groupValuesLabel: 'Name values',
 *       operators: ['=', '!=', ':', '!:'],
 *       getValue: (item) => item.name,
 *     }
 *   ]
 * });
 */
export function usePropertyFilter<T>({
  items,
  filterConfigs,
}: {
  items: T[];
  filterConfigs: FilterPropertyConfig[];
}) {
  const [query, setQuery] = useState<PropertyFilterProps.Query>({ 
    tokens: [], 
    operation: 'and' 
  });

  // Generate filtering properties
  const filteringProperties: PropertyFilterProps.FilteringProperty[] = useMemo(
    () => filterConfigs.map(config => ({
      key: config.key,
      propertyLabel: config.propertyLabel,
      groupValuesLabel: config.groupValuesLabel,
      operators: config.operators || ['=', '!=', ':', '!:'],
    })),
    [filterConfigs]
  );

  // Generate filtering options (autocomplete values)
  const filteringOptions: PropertyFilterProps.FilteringOption[] = useMemo(() => {
    const options: PropertyFilterProps.FilteringOption[] = [];

    filterConfigs.forEach(config => {
      const values = Array.from(
        new Set(
          items
            .map(item => config.getValue(item))
            .filter((value): value is string => Boolean(value))
        )
      );

      values.forEach(value => {
        options.push({
          propertyKey: config.key,
          value: value,
        });
      });
    });

    return options;
  }, [items, filterConfigs]);

  // Apply filtering
  const filteredItems = useMemo(() => {
    if (!query.tokens || query.tokens.length === 0) {
      return items;
    }

    return items.filter(item => {
      const results = query.tokens.map(token => {
        const { propertyKey, operator, value } = token;
        
        // Find the config for this property
        const config = filterConfigs.find(c => c.key === propertyKey);
        if (!config) return true;

        const itemValue = config.getValue(item);
        if (!itemValue) return false;

        const lowerItemValue = itemValue.toLowerCase();
        const lowerValue = value.toLowerCase();

        switch (operator) {
          case '=':
            return lowerItemValue === lowerValue;
          case '!=':
            return lowerItemValue !== lowerValue;
          case ':':
            return lowerItemValue.includes(lowerValue);
          case '!:':
            return !lowerItemValue.includes(lowerValue);
          default:
            return true;
        }
      });

      return query.operation === 'and'
        ? results.every(r => r)
        : results.some(r => r);
    });
  }, [items, query, filterConfigs]);

  return {
    query,
    setQuery,
    filteredItems,
    filteringProperties,
    filteringOptions,
  };
}
